// Create / edit a platform role. Fields: label, key (new roles only), description,
// scope, and a searchable permission picker (catalog grouped by service, checkboxes,
// running selected count) with a live "This role can…" summary. System roles are
// read-only server-side; the caller only opens this drawer for custom roles or new roles.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Form, Input, Select, Button, Space, Checkbox, Collapse, Tag, Typography, Divider,
  Empty, message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  createRole, updateRole, rbacErrorMessage, SCOPE_OPTIONS,
  type PlatformRole, type PermissionCatalog, type CatalogPermission,
} from './rbacService';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  catalog: PermissionCatalog | null;
  // null = create a new role; otherwise edit this (custom) role.
  role: PlatformRole | null;
}

// slugify a label into a candidate key for a brand-new role.
const toKey = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const RoleBuilderDrawer: React.FC<Props> = ({ open, onClose, onSaved, catalog, role }) => {
  const [form] = Form.useForm();
  const [selected, setSelected] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [keyTouched, setKeyTouched] = useState(false);
  const isEdit = !!role;

  useEffect(() => {
    if (!open) return;
    setQ('');
    setKeyTouched(false);
    if (role) {
      form.setFieldsValue({
        label: role.label,
        key: role.key,
        description: role.description,
        scope: role.scope,
      });
      setSelected(role.permissions || []);
    } else {
      form.resetFields();
      setSelected([]);
    }
  }, [open, role, form]);

  const services = catalog?.services || [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filteredServices = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return services;
    return services
      .map((s) => ({
        ...s,
        permissions: (s.permissions || []).filter(
          (p) =>
            p.key.toLowerCase().includes(term) ||
            (p.description || '').toLowerCase().includes(term) ||
            s.service.toLowerCase().includes(term),
        ),
      }))
      .filter((s) => s.permissions.length > 0);
  }, [services, q]);

  const toggle = (key: string, on: boolean) =>
    setSelected((prev) => (on ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));

  const toggleService = (perms: CatalogPermission[], on: boolean) => {
    const keys = perms.map((p) => p.key);
    setSelected((prev) =>
      on
        ? Array.from(new Set([...prev, ...keys]))
        : prev.filter((k) => !keys.includes(k)),
    );
  };

  // Group the current selection back by service for the "This role can…" summary.
  const selectionByService = useMemo(() => {
    const out: { service: string; perms: CatalogPermission[] }[] = [];
    services.forEach((s) => {
      const perms = (s.permissions || []).filter((p) => selectedSet.has(p.key));
      if (perms.length) out.push({ service: s.service, perms });
    });
    return out;
  }, [services, selectedSet]);

  const onLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isEdit && !keyTouched) form.setFieldsValue({ key: toKey(e.target.value) });
  };

  const submit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (selected.length === 0) {
      message.warning('Select at least one permission for this role.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit && role) {
        await updateRole(role.id, {
          label: values.label,
          description: values.description,
          permissions: selected,
        });
        message.success(`Role “${values.label}” updated.`);
      } else {
        await createRole({
          key: values.key,
          label: values.label,
          description: values.description,
          scope: values.scope,
          permissions: selected,
        });
        message.success(`Role “${values.label}” created.`);
      }
      onSaved();
      onClose();
    } catch (e) {
      message.error(rbacErrorMessage(e, 'Could not save the role.'));
    } finally {
      setSaving(false);
    }
  };

  const pickerItems = filteredServices.map((s) => {
    const keys = (s.permissions || []).map((p) => p.key);
    const selCount = keys.filter((k) => selectedSet.has(k)).length;
    const allOn = selCount === keys.length && keys.length > 0;
    return {
      key: s.service,
      label: (
        <Space>
          <Checkbox
            checked={allOn}
            indeterminate={selCount > 0 && !allOn}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => toggleService(s.permissions || [], e.target.checked)}
          />
          <Typography.Text strong>{s.service}</Typography.Text>
          <Tag>{selCount}/{keys.length}</Tag>
        </Space>
      ),
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          {(s.permissions || []).map((p) => (
            <Checkbox
              key={p.key}
              checked={selectedSet.has(p.key)}
              onChange={(e) => toggle(p.key, e.target.checked)}
            >
              <Typography.Text code>{p.key}</Typography.Text>
              {p.dataScope && (
                <Tag style={{ marginInlineStart: 6 }}>{String(p.dataScope)}</Tag>
              )}
              {p.description && (
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {p.description}
                  </Typography.Text>
                </div>
              )}
            </Checkbox>
          ))}
        </Space>
      ),
    };
  });

  return (
    <Drawer
      title={isEdit ? `Edit role: ${role?.label}` : 'New platform role'}
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={submit}>
            {isEdit ? 'Save changes' : 'Create role'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="label"
          label="Label"
          rules={[{ required: true, message: 'Give the role a name' }]}
        >
          <Input placeholder="e.g. Regional Payroll Lead" onChange={onLabelChange} />
        </Form.Item>

        <Form.Item
          name="key"
          label="Key"
          tooltip="Stable machine identifier. Cannot be changed after creation."
          rules={[
            { required: true, message: 'A key is required' },
            {
              pattern: /^[a-z][a-z0-9_]*$/,
              message: 'Lowercase letters, numbers and underscores; must start with a letter',
            },
          ]}
        >
          <Input
            placeholder="e.g. regional_payroll_lead"
            disabled={isEdit}
            onChange={() => setKeyTouched(true)}
          />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="What is this role for?" />
        </Form.Item>

        <Form.Item
          name="scope"
          label="Data scope"
          tooltip="How far this role can see. Capped by the scopes of the permissions it includes."
          rules={[{ required: true, message: 'Pick a data scope' }]}
        >
          <Select
            placeholder="Select a scope"
            options={SCOPE_OPTIONS}
            disabled={isEdit}
          />
        </Form.Item>
        {isEdit && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Key and scope are fixed after creation; edit the label, description and permissions.
          </Typography.Text>
        )}
      </Form>

      <Divider />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          Permissions <Tag color="blue">{selected.length} selected</Tag>
        </Typography.Title>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search permissions…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 280 }}
        />
      </div>

      {pickerItems.length ? (
        <Collapse
          style={{ marginTop: 12 }}
          items={pickerItems}
          defaultActiveKey={q ? pickerItems.map((i) => i.key) : []}
          {...(q ? { activeKey: pickerItems.map((i) => i.key) } : {})}
        />
      ) : (
        <Empty style={{ marginTop: 12 }} description="No permissions match your search" />
      )}

      <Divider />

      <Typography.Title level={5}>This role can…</Typography.Title>
      {selectionByService.length === 0 ? (
        <Typography.Text type="secondary">No permissions selected yet.</Typography.Text>
      ) : (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {selectionByService.map((g) => (
            <div key={g.service}>
              <Typography.Text strong>{g.service}</Typography.Text>
              <div style={{ marginTop: 4 }}>
                {g.perms.map((p) => (
                  <Tag key={p.key} style={{ marginBottom: 4 }}>
                    {p.key}
                  </Tag>
                ))}
              </div>
            </div>
          ))}
        </Space>
      )}
    </Drawer>
  );
};

export default RoleBuilderDrawer;
